/* eslint-disable import/no-extraneous-dependencies */
/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { gsap } from 'gsap';
import Lenis from '@studio-freight/lenis';
import { debounce } from 'lodash';
import Rellax from 'rellax';
import Vue from 'vue';
import FontFaceObserver from 'fontfaceobserver';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Router from './router';
import { ScrollTrigger } from './gsap-bonus/ScrollTrigger';
import { SplitText } from './gsap-bonus/SplitText';

gsap.registerPlugin(ScrollTrigger);

require('./bootstrap');

require('./components/file-upload-component');
require('./components/home-intro-component');
require('./components/job-form-component');
require('./components/jobs-list-component');
require('./components/overlay-component');
require('./components/page-title-component');
require('./components/project-filter-component');
require('./components/project-overview-component');
require('./components/project-detail-header-component');
require('./components/project-cards-component');
require('./components/video-component');
require('./components/services-overview-component');
require('./components/service-header-component');
require('./components/scroll-indicator-component');
require('./components/cookie-message-component');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            // router: null,
            isMobile: false,
            isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            lenis: null,
            popupVisible: false,
            // darkHeader: '1',
            // showFooter: '1'
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },
    },

    mounted() {
        const font = new FontFaceObserver('Poppins');

        this.detectMobile();
        this.overlayEl = document.querySelector('.overlay__wrapper');

        Vue.nextTick(() => {
            font.load(null, 500).then(() => {
                this.initRevealTriggers();
                this.initImageParallax();
                this.initIntroReveal();
                this.initInitialViewportFade();
            }, () => {
                this.initRevealTriggers();
                this.initImageParallax();
                this.initIntroReveal();
                this.initInitialViewportFade();
            });
        });

        this.initSmoothScroll();
        this.initRellax();

        EventBus.$on('disable-body-scoll', (state) => {
            gsap.set([document.querySelector('html'), document.body], {
                delay: state ? 0 : 1,
                overflow: state ? 'hidden' : null,
            });

            if (!this.lenis || this.isMobile) {
                return;
            }

            if (this.lenis.isStopped) {
                this.lenis.start();
                return;
            }

            this.lenis.stop();
        });

        EventBus.$on('project-shadow-toggle', (popupVisible) => {
            this.popupVisible = popupVisible;
        });

        window.addEventListener('resize', this.onWindowResize);
    },

    methods: {
        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
        },

        initSmoothScroll() {
            if (this.isMobile) {
                return;
            }

            this.lenis = new Lenis({
                duration: 1.2,
                easing: (t) => Math.min(1, 1.001 - 1.6 ** (-10 * t)), // https://www.desmos.com/calculator/brs54l4xou
                orientation: 'vertical', // vertical, horizontal
                gestureOrientation: 'vertical', // vertical, horizontal, both
                smoothWheel: true,
                wheelMultiplier: 1,
                smoothTouch: false,
                touchMultiplier: 2,
                infinite: false,
            });

            requestAnimationFrame(this.raf);
        },

        raf(time) {
            this.lenis.raf(time);
            requestAnimationFrame(this.raf);
        },

        initRellax() {
            const rellax = new Rellax('.rellax', {
                speed: -0.5,
            });
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-custom-reveal]'),
            ];

            elements.forEach((element) => {
                const {
                    revealDelay, revealManually, revealPosition, tabletPortraitHome,
                } = element.dataset;

                if (revealManually === '') {
                    return;
                }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (tabletPortraitHome && window.matchMedia('(min-width: 768px) and (max-width: 1023px) and (orientation: portrait)').matches) {
                            gsap.delayedCall(tabletPortraitHome, () => {
                                element.classList.add('--revealed');
                            });

                            return;
                        }

                        if (!revealDelay) {
                            element.classList.add('--revealed');
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            element.classList.add('--revealed');
                        });
                    },
                });
            });

            this.initTitleRevealTriggers();
            this.initLargeTextRevealTriggers();
            this.initIntroTextRevealTriggers();
        },

        initTitleRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-title-reveal]'),
            ];

            this.lineReveals = [];

            elements.forEach((element) => {
                const { revealPosition } = element.dataset;

                const startPercentage = revealPosition || '90%';

                this.titleSplitText = new SplitText(element, { type: 'lines, words', linesClass: 'title-reveal-line' });
                gsap.set(element, { opacity: 1 });

                const linesText = [];

                for (let index = 0; index < this.titleSplitText.lines.length; index++) {
                    const line = this.titleSplitText.lines[index];

                    let inner = line.innerHTML;
                    inner = `<div class="title-reveal-line__text">${inner}</div>`;
                    line.innerHTML = inner;

                    linesText.push(line.querySelector('.title-reveal-line__text'));
                }

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        const revealTl = gsap.timeline();

                        revealTl.to(linesText, {
                            duration: 2, y: 0, stagger: 0.2, ease: 'power4.inOut',
                        }, 0);
                        revealTl.to(element, {
                            duration: 3, y: 0, ease: 'power4.out',
                        }, 0.4);
                    },
                });
            });
        },

        initLargeTextRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-large-text-reveal]'),
            ];

            this.lineReveals = [];

            elements.forEach((element) => {
                const { revealDelay, revealPosition, tabletPortraitHome } = element.dataset;

                const startPercentage = revealPosition || '90%';

                const largeTextSplitText = new SplitText(element, { type: 'lines, words', linesClass: 'large-text-reveal-line' });
                gsap.set(element, { opacity: 1 });

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (tabletPortraitHome && window.matchMedia('(min-width: 768px) and (max-width: 1023px) and (orientation: portrait)').matches) {
                            const revealTl = gsap.timeline({ delay: tabletPortraitHome });

                            revealTl.to(largeTextSplitText.lines, {
                                duration: 1.4, stagger: 0.05, autoAlpha: 1, y: 0, ease: 'power1.inOut',
                            }, 0);
                            return;
                        }

                        if (!revealDelay) {
                            const revealTl = gsap.timeline();

                            revealTl.to(largeTextSplitText.lines, {
                                duration: 1.4, stagger: 0.05, autoAlpha: 1, y: 0, ease: 'power1.inOut',
                            }, 0);
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            const revealTl = gsap.timeline();

                            revealTl.to(largeTextSplitText.lines, {
                                duration: 1.4, stagger: 0.05, autoAlpha: 1, y: 0, ease: 'power1.inOut',
                            }, 0);
                        });
                    },
                });
            });
        },

        initIntroTextRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-intro-text-reveal]'),
            ];

            elements.forEach((element) => {
                for (let index = 0; index < element.children.length; index++) {
                    const child = element.children[index];

                    this.lineReveals = [];

                    const largeRedactorTextSplitText = new SplitText(child, { type: 'lines, words', linesClass: 'large-text-reveal-line' });

                    gsap.set(child, { opacity: 1 });

                    ScrollTrigger.create({
                        trigger: child,
                        start: 'top 100%',
                        onEnter: () => {
                            const revealTl = gsap.timeline();

                            revealTl.to(largeRedactorTextSplitText.lines, {
                                duration: 1.4, stagger: 0.05, autoAlpha: 1, y: 0, ease: 'power1.inOut',
                            }, 0);
                        },
                    });
                }
            });
        },

        initImageParallax() {
            const elements = [
                ...document.querySelectorAll('[data-image-parallax]'),
            ];

            elements.forEach((element) => {
                gsap.to(element.querySelector('.parallax-image'), {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true,
                    },
                    y: '10%',
                    ease: 'none',
                });
            });
        },

        initIntroReveal() {
            if (!document.querySelector('.reveal-intro-holder')) {
                return;
            }

            this.introRevealTl = gsap.timeline({ delay: 0.3 });

            this.introRevealTl.to('.reveal-intro-holder ', {
                duration: 4, y: 0, ease: 'power4.out',
            }, 0.2);
            this.introRevealTl.to('.reveal-intro', {
                duration: 2, autoAlpha: 1, y: 0, ease: 'power4.inOut',
            }, 0);
        },

        initInitialViewportFade() {
            const element = document.querySelector('.initial-viewport-fade');

            if (!element) {
                return;
            }

            if (ScrollTrigger.isInViewport(element)) {
                gsap.set('.initial-viewport-fade', { autoAlpha: 0 });

                gsap.to(element, {
                    duration: 1, autoAlpha: 1, ease: 'power4.inOut', delay: 1,
                });
            }
        },

        toggleProjectFilter() {
            EventBus.$emit('toggle-filter');
        },

        toggleNavigation() {
            if (!this.showNavigation) {
                this.openNavigation();
            } else {
                this.closeNavigation();
            }
        },

        openNavigation() {
            disableBodyScroll(document.querySelector('.navigation'), { reserveScrollBarGap: true });
            this.showNavigation = true;

            if (!this.openNavigationTl) {
                this.openNavigationTl = gsap.timeline({ paused: true });

                this.openNavigationTl.to('.navigation__page-overlay', { duration: 0.5, autoAlpha: 0.75, ease: 'none' }, 0);

                if (window.matchMedia('(min-width: 1440px)').matches) {
                    this.openNavigationTl.to('.header__hamburger-line--first', {
                        duration: 0.8, rotation: -45, x: -3, y: 5, ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__hamburger-line--last', {
                        duration: 0.8, rotation: 45, x: -3, y: -6, ease: 'power4.out',
                    }, 0);
                } else if (window.matchMedia('(min-width: 1280px)').matches) {
                    this.openNavigationTl.to('.header__hamburger-line--first', {
                        duration: 0.8, rotation: -45, x: -3, y: 4, ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__hamburger-line--last', {
                        duration: 0.8, rotation: 45, x: -3, y: -5, ease: 'power4.out',
                    }, 0);
                } else if (window.matchMedia('(min-width: 1024px)').matches) {
                    this.openNavigationTl.to('.header__hamburger-line--first', {
                        duration: 0.8, rotation: -45, x: -2, y: 3, ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__hamburger-line--last', {
                        duration: 0.8, rotation: 45, x: -2, y: -4, ease: 'power4.out',
                    }, 0);
                } else if (window.matchMedia('(min-width: 768px)').matches) {
                    this.openNavigationTl.to('.header__hamburger-line--first', {
                        duration: 0.8, rotation: -45, x: -3, y: 5, ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__hamburger-line--last', {
                        duration: 0.8, rotation: 45, x: -3, y: -5, ease: 'power4.out',
                    }, 0);
                } else {
                    this.openNavigationTl.to('.header__hamburger-line--first', {
                        duration: 0.8, rotation: -45, x: -2, y: 3, ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__hamburger-line--last', {
                        duration: 0.8, rotation: 45, x: -2, y: -4, ease: 'power4.out',
                    }, 0);
                }

                if (window.matchMedia('(min-width: 1024px)').matches) {
                    this.openNavigationTl.to('.navigation__background', { duration: 1, scaleX: 1, ease: 'power4.out' }, 0.1);
                } else {
                    this.openNavigationTl.to('.navigation__background', { duration: 1, scaleY: 1, ease: 'power4.out' }, 0.1);
                }

                this.openNavigationTl.to('.navigation__content-socials ', {
                    duration: 3, y: 0, ease: 'power4.out',
                }, 0);
                this.openNavigationTl.to('.navigation__content-socials-border', {
                    duration: 3, autoAlpha: 0.3, ease: 'power4.out',
                }, 0);
                this.openNavigationTl.to('.navigation__content-socials-text', {
                    duration: 1.5, y: 0, ease: 'power4.inOut',
                }, 0);

                if (document.body.classList.contains('dark')) {
                    this.openNavigationTl.to(['.header__hamburger-line', '.header__language-switch-divider'], {
                        duration: 1.4, backgroundColor: '#1a1a18', ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__language-switch-link', {
                        duration: 1.4, color: '#1a1a18', ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__scrollbar-background', {
                        duration: 1.4, backgroundColor: 'rgba(26, 26, 24, 0.3)', ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header__scrollbar-indicator', {
                        duration: 1.4, backgroundColor: 'rgba(26, 26, 24)', ease: 'power4.out',
                    }, 0);
                    this.openNavigationTl.to('.header .white', {
                        duration: 1.4, fill: '#1a1a18', ease: 'power4.out',
                    }, 0);
                    // this.openNavigationTl.to('.header', {
                    //     duration: 1.4, backgroundColor: '#fff', ease: 'power4.out',
                    // }, 0);
                } else if (window.matchMedia('(max-width: 1023px)').matches) {
                    this.openNavigationTl.to('.header', {
                        duration: 0.6, backgroundColor: '#fff', ease: 'power4.out',
                    }, 0);
                }

                this.openNavigationTl.to('.navigation__content-socials-inner', {
                    duration: 1.5, y: 0, ease: 'power4.inOut',
                }, 0);
                this.openNavigationTl.to('.navigation__content-logo-holder', {
                    duration: 3, y: 0, ease: 'power4.out',
                }, 0.2);
                this.openNavigationTl.to('.navigation__content-logo', {
                    duration: 1.5, y: 0, ease: 'power4.inOut',
                }, 0);
                this.openNavigationTl.to('.navigation__content-nav ', {
                    duration: 3, y: 0, ease: 'power4.out',
                }, 0.1);
                this.openNavigationTl.to('.navigation__content-nav-list-item-link', {
                    duration: 1.5, stagger: 0.1, y: 0, ease: 'power4.inOut',
                }, 0.1);
            }

            this.openNavigationTl.play(0);
        },

        closeNavigation() {
            if (!this.closeNavigationTl) {
                this.closeNavigationTl = gsap.timeline({
                    paused: true,
                });
            }

            this.closeNavigationTl.to('.navigation__content-logo', {
                duration: 1, y: '100%', ease: 'power4.inOut',
            }, 0);
            this.closeNavigationTl.to('.header__hamburger-line--first', {
                duration: 0.8, rotation: 0, x: 0, y: 0, ease: 'power4.out',
            }, 0);
            this.closeNavigationTl.to('.header__hamburger-line--last', {
                duration: 0.8, rotation: 0, x: 0, y: 0, ease: 'power4.out',
            }, 0);
            this.closeNavigationTl.to('.navigation__content-nav-list-item-link', { duration: 0.8, y: '100%', ease: 'power4.inOut' }, 0);
            this.closeNavigationTl.to('.navigation__content-socials-text', {
                duration: 1, y: '100%', ease: 'power4.inOut',
            }, 0);
            this.closeNavigationTl.to('.navigation__content-socials-inner', {
                duration: 0.8, y: '100%', ease: 'power4.inOut',
            }, 0);
            this.closeNavigationTl.to('.navigation__content-socials-border', {
                duration: 1, autoAlpha: 0, ease: 'power4.inOut',
            }, 0);

            if (window.matchMedia('(min-width: 1024px)').matches) {
                this.closeNavigationTl.to('.navigation__background', {
                    duration: 1,
                    scaleX: 0,
                    ease: 'power2.inOut',
                    onComplete: () => {
                        enableBodyScroll(document.querySelector('.navigation'));
                        this.showNavigation = false;
                    },
                }, 0.2);
            } else {
                this.closeNavigationTl.to('.navigation__background', {
                    duration: 1,
                    scaleY: 0,
                    ease: 'power2.inOut',
                    onComplete: () => {
                        enableBodyScroll(document.querySelector('.navigation'));
                        this.showNavigation = false;
                    },
                }, 0.2);
            }

            this.closeNavigationTl.to('.navigation__page-overlay', { duration: 0.5, autoAlpha: 0, ease: 'none' }, 0.7);

            if (document.body.classList.contains('dark')) {
                this.closeNavigationTl.to(['.header__hamburger-line', '.header__language-switch-divider'], {
                    duration: 1, backgroundColor: '#fff', ease: 'power4.out',
                }, 0.8);
                this.closeNavigationTl.to('.header__language-switch-link', {
                    duration: 1, color: '#fff', ease: 'power4.out',
                }, 0.8);
                this.closeNavigationTl.to('.header__scrollbar-background', {
                    duration: 1, backgroundColor: 'rgba(255, 255, 255, 0.3)', ease: 'power4.out',
                }, 0.8);
                this.closeNavigationTl.to('.header__scrollbar-indicator', {
                    duration: 1, backgroundColor: 'rgba(255, 255, 255)', ease: 'power4.out',
                }, 0.8);
                this.closeNavigationTl.to('.header .white', {
                    duration: 1, fill: '#fff', ease: 'power4.out',
                }, 0.8);
                // this.closeNavigationTl.to('.header', {
                //     duration: 0.4, backgroundColor: '#1a1a18', ease: 'power4.out',
                // }, 0.9);
            } else if (window.matchMedia('(max-width: 1023px)').matches) {
                this.closeNavigationTl.to('.header', {
                    duration: 0.4, backgroundColor: '#f5f5f4', ease: 'power4.out',
                }, 0.9);
            }

            if (this.openNavigationTl.isActive()) {
                this.openNavigationTl.kill();
            }

            Vue.nextTick(() => {
                this.closeNavigationTl.play(0);
            });
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
